// Ember Nebula fragment shader — lifted verbatim from the nav-handoff bundle.
// Domain-warped FBM smoke field with 18 hash-driven embers orbiting an
// attractor point (mouse or Lissajous auto-drift).
const HEAD = `
precision highp float;
uniform vec2  u_res;
uniform float u_time;
uniform vec2  u_mouse;
uniform vec2  u_smooth;
uniform vec2  u_click;
uniform float u_clickT;
uniform float u_down;
#define PI 3.14159265359
vec2 aspectUV() {
  return (gl_FragCoord.xy - 0.5*u_res.xy) / u_res.y;
}
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p);
  vec2 u = f*f*(3.0-2.0*f);
  float a = hash(i);
  float b = hash(i+vec2(1,0));
  float c = hash(i+vec2(0,1));
  float d = hash(i+vec2(1,1));
  return mix(mix(a,b,u.x), mix(c,d,u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0; float a = 0.5;
  for (int i = 0; i < 5; i++) { v += a*noise(p); p *= 2.02; a *= 0.5; }
  return v;
}
`;

export const EMBER_NEBULA = HEAD + `
void main() {
  vec2 uv = aspectUV();
  vec2 m = (u_smooth - 0.5) * vec2(u_res.x/u_res.y, 1.0);

  float t = u_time * 0.15;

  vec2 d = uv - m;
  float r = length(d);
  float ang = atan(d.y, d.x);
  ang += 0.6 / (r + 0.2) + t;
  vec2 swirl = m + vec2(cos(ang), sin(ang)) * r;

  float n = fbm(swirl*1.4 + vec2(t, -t*0.5));
  n = pow(n, 1.6);

  vec3 smoke = mix(vec3(0.02, 0.01, 0.02), vec3(0.55, 0.15, 0.05), n);
  smoke = mix(smoke, vec3(1.0, 0.55, 0.15), pow(n, 3.0));

  vec3 emberCol = vec3(0.0);
  for (int i = 0; i < 18; i++) {
    float fi = float(i);
    float seed = fi*1.37;
    float orbitR = 0.15 + 0.6 * hash(vec2(seed, 3.1));
    float orbitS = (0.3 + hash(vec2(seed, 7.2))*1.2) * (mod(fi,2.0) < 0.5 ? 1.0 : -1.0);
    float ph = hash(vec2(seed, 9.9)) * 6.2831;
    vec2 ep = m + vec2(cos(u_time*orbitS + ph), sin(u_time*orbitS + ph)) * orbitR;
    ep += 0.05 * vec2(sin(u_time*0.3 + seed), cos(u_time*0.2 + seed*1.3));

    float ed = length(uv - ep);
    float glow = exp(-ed*40.0) * 1.6 + exp(-ed*10.0) * 0.25;
    glow *= 0.75 + 0.25*sin(u_time*8.0 + seed*5.0);
    vec3 ec = mix(vec3(1.0, 0.6, 0.15), vec3(1.0, 0.9, 0.5), hash(vec2(seed,1.1)));
    emberCol += ec * glow;
  }

  vec2 cp = (u_click-0.5)*vec2(u_res.x/u_res.y, 1.0);
  float cd = length(uv - cp);
  float ring = smoothstep(0.04, 0.0, abs(cd - u_clickT*1.2)) * exp(-u_clickT*1.5);
  float flash = exp(-u_clickT*4.0) * exp(-cd*2.5);
  vec3 shock = vec3(1.0, 0.7, 0.3) * (ring*1.2 + flash*0.6);

  vec3 core = vec3(1.0, 0.55, 0.2) * exp(-r*5.0) * (0.25 + u_down*0.4);

  vec3 col = smoke * 0.9 + emberCol + shock + core;
  col += (hash(gl_FragCoord.xy + u_time) - 0.5) * 0.015;
  col *= 1.0 - 0.5 * pow(length(uv), 2.0);

  gl_FragColor = vec4(col, 1.0);
}
`;
